import { useState, useEffect } from 'react';
import { COMMON_COLORS } from '../../constants/themeDefaults';
import { hexToRgb, rgbToHex, meetsWCAG_AA } from '../../utils/themeUtils';

/**
 * Color Picker Component
 *
 * Advanced color selection with:
 * - HTML5 color input
 * - Hex input field
 * - Common color swatches
 * - WCAG contrast checking
 */
export default function ColorPicker({
  label,
  value = '#000000',
  onChange,
  showContrast = false,
  contrastBackground = '#ffffff',
  disabled = false
}) {
  const [hexInput, setHexInput] = useState(value);
  const [isValidHex, setIsValidHex] = useState(true);

  useEffect(() => {
    setHexInput(value);
  }, [value]);

  const handleHexChange = (newHex) => {
    setHexInput(newHex);

    // Validate hex color
    const hexRegex = /^#[0-9A-Fa-f]{6}$/;
    if (hexRegex.test(newHex)) {
      setIsValidHex(true);
      onChange(newHex);
    } else {
      setIsValidHex(false);
    }
  };

  const handleColorPickerChange = (e) => {
    const newColor = e.target.value;
    setHexInput(newColor);
    setIsValidHex(true);
    onChange(newColor);
  };

  const handleSwatchClick = (color) => {
    setHexInput(color);
    setIsValidHex(true);
    onChange(color);
  };

  // Calculate contrast ratio if needed
  const contrastPasses = showContrast ? meetsWCAG_AA(value, contrastBackground) : null;

  return (
    <div className="space-y-2">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-300">
          {label}
        </label>
      )}

      <div className="flex items-center gap-3">
        {/* HTML5 Color Picker */}
        <div className="relative">
          <input
            type="color"
            value={value}
            onChange={handleColorPickerChange}
            disabled={disabled}
            className={`
              w-12 h-12 rounded-lg cursor-pointer
              border-2 border-gray-700
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all hover:scale-105
            `}
            style={{
              backgroundColor: value
            }}
          />
          {/* Color preview overlay */}
          <div
            className="absolute inset-0 rounded-lg pointer-events-none border-2 border-gray-600"
            style={{ backgroundColor: value }}
          />
        </div>

        {/* Hex Input */}
        <div className="flex-1">
          <input
            type="text"
            value={hexInput}
            onChange={(e) => handleHexChange(e.target.value)}
            disabled={disabled}
            placeholder="#000000"
            className={`
              w-full px-3 py-2 rounded-lg
              bg-gray-800 border
              ${isValidHex ? 'border-gray-700' : 'border-red-500'}
              text-white font-mono text-sm
              focus:outline-none focus:ring-2
              ${isValidHex ? 'focus:ring-purple-500/50' : 'focus:ring-red-500/50'}
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all
            `}
          />
          {!isValidHex && (
            <p className="text-xs text-red-400 mt-1">
              Invalid hex color format
            </p>
          )}
        </div>

        {/* RGB Display */}
        <div className="px-3 py-2 bg-gray-800/50 rounded-lg border border-gray-700/50">
          <p className="text-xs text-gray-400 font-mono">
            {(() => {
              const rgb = hexToRgb(value);
              return rgb ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : 'Invalid';
            })()}
          </p>
        </div>
      </div>

      {/* Color Swatches */}
      <div>
        <p className="text-xs text-gray-400 mb-2">Quick Colors</p>
        <div className="flex flex-wrap gap-2">
          {COMMON_COLORS.map((color) => (
            <button
              key={color}
              onClick={() => handleSwatchClick(color)}
              disabled={disabled}
              className={`
                w-8 h-8 rounded-md border-2 transition-all
                ${value === color ? 'border-white scale-110' : 'border-gray-600'}
                hover:scale-110 hover:border-gray-400
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>
      </div>

      {/* Contrast Check */}
      {showContrast && (
        <div className={`
          p-3 rounded-lg border
          ${contrastPasses ? 'bg-green-900/20 border-green-700/50' : 'bg-red-900/20 border-red-700/50'}
        `}>
          <div className="flex items-center gap-2">
            {contrastPasses ? (
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${contrastPasses ? 'text-green-400' : 'text-red-400'}`}>
                {contrastPasses ? 'WCAG AA Compliant' : 'Contrast Too Low'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {contrastPasses
                  ? 'This color has sufficient contrast for accessibility'
                  : 'This color may be hard to read for some users'
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
